import { useState, useEffect, useRef, useContext } from "react";
import DOMPurify from "dompurify";
import _ from "lodash";
import {Popup} from "react-map-gl";
import { Img } from "react-image";
import { ThemeContext } from "../../../Context/ThemeContext";

import styles from "./MapMarkerPopup.module.scss";

const ReadMore = ({ children }) => {
  const htmlContent = children;
  // Sanitize the HTML content to strip out all HTML tags, leaving only plain text
  const cleanText = DOMPurify.sanitize(htmlContent, { ALLOWED_TAGS: [] });
  const [isReadMore, setIsReadMore] = useState(true);

  const toggleReadMore = () => {
    setIsReadMore(!isReadMore);
  };

  const renderContentWithBreaks = (full = false) => {
    // Replace escaped newlines with HTML line breaks before sanitizing
    const preProcessedHtml = htmlContent.replace(/\\n/g, "<br />");
    const allowedTags = ["br", "p"];
    const sanitizedHtml = DOMPurify.sanitize(preProcessedHtml, {
      ALLOWED_TAGS: allowedTags,
    });

    const contentToShow = full
      ? sanitizedHtml
      : `${sanitizedHtml.slice(0, 170)}...`;

    return { __html: contentToShow };
  };

  return (
    <p className="text">
      {cleanText.length > 170 ? (
        <>
          {isReadMore ? (
            <div dangerouslySetInnerHTML={renderContentWithBreaks()} />
          ) : (
            <div dangerouslySetInnerHTML={renderContentWithBreaks(true)} />
          )}
          <span
            onClick={toggleReadMore}
            className={styles["read-or-hide"]}
            style={{ color: "blue", cursor: "pointer" }}
          >
            {isReadMore ? "Read more" : "Show less"}
          </span>
        </>
      ) : (
        <div dangerouslySetInnerHTML={renderContentWithBreaks(true)} />
      )}
    </p>
  );
};

const MapMarkerPopup = (props) => {
  const {
    destinationEvent,
    travelPlanId,
    destinationGroup,
    selectedDateTime,
    setViewport,
    forceShowPopup,
  } = props;

  const [showPopup, togglePopup] = useState(false);

  const ref = useRef();
  const tempTimeoutRef = useRef(null);

  const theme = useContext(ThemeContext);

  const latitude = destinationEvent?.location?.latitude;
  const longitude = destinationEvent?.location?.longitude;

  useEffect(() => {
    togglePopup(true);
    setViewport(latitude, longitude, 14);
  }, []);

  useEffect(() => {
    if (forceShowPopup) {
      togglePopup(true);
    }
  }, [forceShowPopup]);

  function updateCloseCallBackFunction() {
    togglePopup(false);
    props.updateCloseCallBack();
  }

  const processMapMarkerLinkUrl = (linkUrl, options) => {
    if (!linkUrl || linkUrl === "") return linkUrl;
    if (!options || _.isEmpty(options)) return linkUrl;

    linkUrl += linkUrl.includes("?") ? "" : "?";

    if (options.addDestinationGroupName) {
      const key = options.addDestinationGroupNameKey ?? "ystid";
      const value = destinationGroup.name;
      linkUrl += `&${key}=${value}`;
    }

    if (options.addEnvironmentName) {
      const key = options.addEnvironmentNameKey ?? "env";
      let url = window.location.href,
        startStr = "//",
        endStr = ".yousmartthing.com";
      let startIndex = url.indexOf(startStr),
        endIndex = url.indexOf(endStr);
      let value =
        startIndex > -1 && endIndex > -1
          ? url.substring(startIndex + startStr.length, endIndex)
          : "travel";
      linkUrl += `&${key}=${value}`;
    }

    return linkUrl;
  };

  const processMapMarker = (mapMarker) => {
    if (mapMarker === null) return mapMarker;
    mapMarker.linkUrl = processMapMarkerLinkUrl(
      mapMarker.linkUrl,
      mapMarker.linkUrlOptions
    );
    return mapMarker;
  };

  const createMapContent = () => {
    // Props 'image' and 'imageName' accommodate legacy data values.
    let legacyImageUrl =
      destinationEvent && destinationEvent.image
        ? destinationEvent.image
        : null;

    const mapContent =
      destinationEvent && destinationEvent.mapContent
        ? { ...destinationEvent.mapContent }
        : {
            mapMarker: {
              name: name,
              imageUrl: legacyImageUrl,
              linkUrl: "",
              linkUrlOptions: {},
              customHtml: "",
              customJson: {},
              tags: [],
            },
            confirmationPage: null,
            email: null,
            sms: null,
          };

    mapContent.mapMarker = processMapMarker(mapContent.mapMarker);

    return mapContent;
  };

  const name =
    destinationEvent && destinationEvent.imageName
      ? destinationEvent.imageName
      : destinationEvent.name;

  const availabilityText =
    destinationEvent && destinationEvent.status
      ? "(" + destinationEvent.status + ")"
      : "";
  const mapContent = createMapContent();
  const { customHtml, imageUrl, linkUrl } = mapContent.mapMarker;

  var { customJson } = mapContent.mapMarker;
  if (
    (customJson === undefined || customJson === null) &&
    destinationGroup !== undefined &&
    destinationGroup !== null
  ) {
    customJson = destinationGroup.customJson;
  }

  const tipSize = destinationEvent ? 5 : null;

  const renderImageUrl =
    imageUrl && imageUrl !== "" ? (
      <Img
        className={styles["pop-up-image"]}
        alt={"Marker popup " + name}
        width={220}
        src={imageUrl}
      />
    ) : (
      ""
    );

  function renderTXGB() {
    return (
      destinationEvent &&
      destinationEvent.isTXGBavailable && (
        <button
          type="button"
          className={styles["button-txgb"]}
          onClick={() =>
            open(
              process.env.REACT_APP_TXGB_URL +
                destinationEvent.uniqueIdentifier +
                process.env.REACT_APP_TXGB_URL_OPTIONS +
                selectedDateTime
            )
          }
        >
          <span className={styles["button-span"]}>Book a room</span>
        </button>
      )
    );
  }

  function open(url) {
    const win = window.open(url, "_blank");
    if (win != null) {
      win.focus();
    }
  }

  function renderCustomHtml() {
    if (travelPlanId != null) {
      if (customHtml != null && customHtml.includes("ystid")) {
        var myArr = customHtml.split("ystid");
        var myArr2 = myArr[1].split("'");
        myArr2.shift();
        const key = "travelPlanId";
        const value = travelPlanId;
        const key2 = "env";
        let url = window.location.href,
          startStr = "//",
          endStr = ".yousmartthing.com";
        let startIndex = url.indexOf(startStr),
          endIndex = url.indexOf(endStr);
        let value2 =
          startIndex > -1 && endIndex > -1
            ? url.substring(startIndex + startStr.length, endIndex)
            : "travel";
        myArr[0] += `&${key}=${value}&${key2}=${value2}''` + myArr2.join("'");
      }
    }

    return customHtml && customHtml !== "" ? (
      <div className={styles["pop-up-custom"]}>
        {destinationGroup.showReadMoreTag ? (
          <ReadMore>{customHtml}</ReadMore>
        ) : (
          <>
            <div style={{ position: "relative" }}>
              <style>
                {`
                  .custom-html a {
                    color: ${theme.theme.primaryColour};
                    text-decoration: underline;
                    font-weight: bold;
                  }
                `}
              </style>
              <div
                className="custom-html"
                dangerouslySetInnerHTML={{ __html: customHtml }}
              />
            </div>
          </>
        )}
      </div>
    ) : (
      ""
    );
  }

  return (
    <div>
      {showPopup && latitude && longitude && (
        <Popup
          className={styles["mapboxgl-popup"]}
          anchor="top"
          tipSize={tipSize}
          longitude={longitude}
          latitude={latitude}
          closeButton={true}
          closeOnClick={false}
          onClose={() => updateCloseCallBackFunction()}
          focusAfterOpen={false}
        >
          <div ref={ref}>
            {destinationEvent?.customURL &&
            destinationEvent?.customURLPosition === "title" ? (
              <a
                href={destinationEvent.customURL}
                target="_blank"
                rel="noreferrer"
              >
                <div className={styles["pop-up-inner"]}>
                  {destinationGroup?.name === "EV Assist" ? "" : name}{" "}
                  {availabilityText}
                </div>{" "}
              </a>
            ) : (
              <div className={styles["pop-up-inner"]}>
                {destinationGroup?.name === "EV Assist" ? "" : name}{" "}
                {availabilityText}
              </div>
            )}

            {linkUrl !== "" ? (
              <a
                href={linkUrl}
                target="_blank"
                rel="noreferrer"
              >
                {renderImageUrl}
              </a>
            ) : (
              renderImageUrl
            )}
            {renderCustomHtml()}
            {renderTXGB()}
          </div>
        </Popup>
      )}
    </div>
  );
};

export default MapMarkerPopup;
